import { ApiResponse } from 'apisauce';
import { ErrorInfo } from '../shared/interfaces/error-info.interface';
import { ResponseModel } from '../shared/interfaces/response-model.interface';
import LandlordMapper from '../shared/mappers/landlord.mapper';
import {
  ImportResultModel,
  ImportRowResultModel,
  LandlordDetailModel,
  LandlordListModel
} from '../shared/models/landlord.model';
import api from '../shared/services/utils/api/api.service';
import errorHelper from '../shared/services/utils/api/error-helper.service';
import {
  BankAccountFormUIModel,
  LandlordDetailUIModel,
  LandlordListUIModel
} from '../shared/ui-models/landlord.ui-model';

const MODULE_NAME = 'LANDLORD_MODULE';

function toError(response: ApiResponse<any, ErrorInfo>, displayName: string) {
  const body: any = response.data;

  const baseError = errorHelper(
    Number(response.status),
    body && body.errorCode ? String(body.errorCode || '') : String(response.problem || ''),
    MODULE_NAME,
    displayName
  );

  // The backend's own message ("The file has no data rows") is more specific than the
  // generic per-status copy in error.config.ts, so prefer it whenever the API supplies one.
  if (body && body.message) {
    return { ...baseError, errorMessage: body.message };
  }

  return baseError;
}

export interface LandlordListParams {
  page: number;
  pageSize: number;
  search?: string;
  bankDetailsMissing?: boolean;
  sortBy?: string;
  sortDirection?: string;
}

export function getLandlords(params: LandlordListParams): Promise<LandlordListUIModel | ErrorInfo> {
  return api
    .getLandlords(params)
    .then((response: ApiResponse<ResponseModel<LandlordListModel>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return LandlordMapper.mapListToUI(response.data.data);
      }
      return toError(response, (api.getLandlords as any).displayName);
    });
}

export function getLandlordDetail(landlordId: string): Promise<LandlordDetailUIModel | ErrorInfo> {
  return api
    .getLandlordDetail(landlordId)
    .then((response: ApiResponse<ResponseModel<LandlordDetailModel>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return LandlordMapper.mapDetailToUI(response.data.data);
      }
      return toError(response, (api.getLandlordDetail as any).displayName);
    });
}

export interface BankAccountSaveError {
  fieldErrors: { field: string; message: string }[];
  message: string;
}

export function isBankAccountSaveError(value: any): value is BankAccountSaveError {
  return !!value && Array.isArray(value.fieldErrors);
}

export function saveBankAccount(
  landlordId: string,
  form: BankAccountFormUIModel
): Promise<true | BankAccountSaveError | ErrorInfo> {
  return api
    .upsertLandlordBankAccount(landlordId, LandlordMapper.mapFormToModel(form))
    .then((response: ApiResponse<ResponseModel<unknown>, ErrorInfo>) => {
      if (response.ok) {
        return true as const;
      }

      const body: any = response.data;
      if (response.status === 422 && body && Array.isArray(body.data)) {
        return {
          fieldErrors: body.data,
          message: body.message || 'Bank account details are invalid'
        };
      }

      return toError(response, (api.upsertLandlordBankAccount as any).displayName);
    });
}

function triggerBrowserDownload(blob: Blob, fallbackName: string, headers?: any) {
  const disposition = headers && (headers['content-disposition'] || headers['Content-Disposition']);
  let filename = fallbackName;

  if (disposition) {
    const match = /filename="?([^";]+)"?/.exec(String(disposition));
    if (match && match[1]) {
      filename = match[1];
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function downloadTemplate(): Promise<true | ErrorInfo> {
  return api.downloadBankDetailsTemplate().then((response: ApiResponse<Blob, ErrorInfo>) => {
    if (response.ok && response.data) {
      triggerBrowserDownload(response.data, 'landlord-bank-details.xlsx', response.headers);
      return true as const;
    }
    return toError(response, (api.downloadBankDetailsTemplate as any).displayName);
  });
}

export function downloadImportResult(
  rows: ImportRowResultModel[],
  format: 'xlsx' | 'csv'
): Promise<true | ErrorInfo> {
  return api
    .downloadBankDetailsResult(rows, format)
    .then((response: ApiResponse<Blob, ErrorInfo>) => {
      if (response.ok && response.data) {
        triggerBrowserDownload(
          response.data,
          `bank-details-import-result.${format}`,
          response.headers
        );
        return true as const;
      }
      return toError(response, (api.downloadBankDetailsResult as any).displayName);
    });
}

function handleImportResponse(
  response: ApiResponse<ResponseModel<ImportResultModel>, ErrorInfo>,
  displayName: string
): ImportResultModel | ErrorInfo {
  if (response.ok && response.data && response.data.data) {
    return response.data.data;
  }
  return toError(response, displayName);
}

export function validateImport(file: File): Promise<ImportResultModel | ErrorInfo> {
  return api
    .validateBankDetailsImport(file)
    .then((response) =>
      handleImportResponse(response, (api.validateBankDetailsImport as any).displayName)
    );
}

export function applyImport(file: File): Promise<ImportResultModel | ErrorInfo> {
  return api
    .applyBankDetailsImport(file)
    .then((response) =>
      handleImportResponse(response, (api.applyBankDetailsImport as any).displayName)
    );
}
